/**
 * Setting a case up: one action at a time against the setup endpoints,
 * each answering with the setup read again — the parts read what came back.
 * A refusal says where the case stands (the API's reason) and reads the
 * setup again, since the case moved.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { ApiConflictError, ApiValidationError } from '@/services/vnext-api';
import { toError } from '@/utils/errors';

export interface SetupActions {
  reportId: string;
  /** A person may act: live and not started. */
  editable: boolean;
  /** Partner work may still be approved, added or re-assigned: live and,
   * once started, not past partner work. */
  partnerWorkEditable: boolean;
  /** The commercials move at any point while the case is live. */
  commercialsEditable: boolean;
  /** The case has started. */
  started: boolean;
  /** An evaluation copy: its partner work is never dispatched. */
  evaluationCopy: boolean;
  /** The action running, by name. */
  pending: string | null;
  /** Run an action; whether it saved. */
  run: (name: string, call: () => Promise<unknown>, done?: string) => Promise<boolean>;
  /** The case moved: read the setup (and the case) again. */
  refresh: () => Promise<void>;
}

const ActionsContext = createContext<SetupActions | null>(null);

export function useSetupActions(): SetupActions {
  const actions = useContext(ActionsContext);
  if (!actions) {
    throw new Error('useSetupActions outside SetupActionsProvider');
  }
  return actions;
}

/** A refusal, in words: the API's message, with its reason where that says
 * more. */
export function refusalMessage(err: unknown): string {
  if (err instanceof ApiConflictError || err instanceof ApiValidationError) {
    return err.message;
  }
  return toError(err, 'Not saved').message;
}

export function SetupActionsProvider({
  reportId,
  editable,
  partnerWorkEditable = editable,
  commercialsEditable = editable,
  started = false,
  evaluationCopy = false,
  onChanged,
  children,
}: Readonly<{
  reportId: string;
  editable: boolean;
  partnerWorkEditable?: boolean;
  commercialsEditable?: boolean;
  started?: boolean;
  evaluationCopy?: boolean;
  onChanged: () => Promise<void>;
  children: ReactNode;
}>) {
  const [pending, setPending] = useState<string | null>(null);
  const run = useCallback(
    async (name: string, call: () => Promise<unknown>, done?: string) => {
      setPending(name);
      try {
        await call();
        if (done) {
          notifications.show({ color: 'teal', message: done });
        }
        await onChanged();
        return true;
      } catch (err) {
        if (!(err instanceof ApiConflictError) && !(err instanceof ApiValidationError)) {
          Sentry.captureException(toError(err));
        }
        notifications.show({ color: 'red', message: refusalMessage(err) });
        if (err instanceof ApiConflictError) {
          await onChanged();
        }
        return false;
      } finally {
        setPending(null);
      }
    },
    [onChanged]
  );
  const value = useMemo(
    () => ({
      reportId,
      editable,
      partnerWorkEditable,
      commercialsEditable,
      started,
      evaluationCopy,
      pending,
      run,
      refresh: onChanged,
    }),
    [
      reportId,
      editable,
      partnerWorkEditable,
      commercialsEditable,
      started,
      evaluationCopy,
      pending,
      run,
      onChanged,
    ]
  );
  return <ActionsContext.Provider value={value}>{children}</ActionsContext.Provider>;
}
